// Coverage Map API client. Mirrors lib/threatModelFindingsApi.ts so the
// surface stays consistent across the resolver/structure panels.

import type { DocumentStructureResponse } from '@/types/documentStructure'

const API_BASE = '/api'

function getOrCreateClientId(): string {
  const KEY = 'ai_alm.client_id'
  try {
    let id = window.localStorage.getItem(KEY)
    if (!id) {
      id = `c_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`
      window.localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'c_anonymous'
  }
}

function newRequestId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`
}

export class DocumentStructureError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'DocumentStructureError'
    this.status = status
  }
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': getOrCreateClientId(),
      'X-Request-Id': newRequestId(),
      ...(init?.headers ?? {}),
    },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const envelope = await response.json()
      detail =
        envelope?.error?.message ??
        envelope?.detail ??
        response.statusText ??
        `HTTP ${response.status}`
    } catch {
      detail = response.statusText || `HTTP ${response.status}`
    }
    // Carry the status so the panel can treat 404 (flag off / route not
    // mounted) as "hide" rather than "error".
    throw new DocumentStructureError(detail, response.status)
  }
  return (await response.json()) as T
}

export function getDocumentStructure(
  projectId: string,
  runId: number,
): Promise<DocumentStructureResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/document-structure`,
  )
}
